/**
 * NanoClaw Web 应用入口
 */
import { API_BASE, WS_URL, fetchAppConfig } from './config.js';
import { LoginScreen } from './components/LoginScreen.js';
import { ChatApp } from './components/ChatApp.js';
import { SettingsPanel } from './components/SettingsPanel.js';
import { AgentMonitor } from './components/AgentMonitor.js';
import { storage } from './utils/storage.js';

// 应用名称（从后端获取）
let appName = 'NanoClaw';

// 应用状态
interface AppState {
  token: string | null;
  chatJid: string | null;
  isAuthenticated: boolean;
}

const state: AppState = {
  token: storage.getToken(),
  chatJid: storage.getChatJid(),
  isAuthenticated: false,
};

// 组件实例
let loginScreen: LoginScreen | null = null;
let chatApp: ChatApp | null = null;
let settingsPanel: SettingsPanel | null = null;
let agentMonitor: AgentMonitor | null = null;

/**
 * 初始化应用
 */
async function init(): Promise<void> {
  // 获取应用配置
  try {
    const config = await fetchAppConfig();
    appName = config.appName;
    updatePageTitle();
  } catch (e) {
    console.error('Failed to fetch app config:', e);
  }

  // 检查认证状态
  if (state.token) {
    state.isAuthenticated = true;
    showChatApp();
  } else {
    showLoginScreen();
  }
}

/**
 * 更新页面标题
 */
function updatePageTitle(): void {
  document.title = `${appName} - Web Chat`;
}

/**
 * 显示登录界面
 */
function showLoginScreen(): void {
  if (chatApp) {
    chatApp.destroy();
    chatApp = null;
  }

  loginScreen = new LoginScreen({
    appName,
    onLoginSuccess: (token, chatJid) => {
      state.token = token;
      state.chatJid = chatJid;
      state.isAuthenticated = true;

      storage.setToken(token);
      storage.setChatJid(chatJid);

      loginScreen?.hide();
      showChatApp();
    },
  });

  loginScreen.show();
}

/**
 * 显示聊天应用
 */
function showChatApp(): void {
  if (!state.token) {
    showLoginScreen();
    return;
  }

  chatApp = new ChatApp({
    token: state.token,
    chatJid: state.chatJid,
    apiBaseUrl: API_BASE,
    wsUrl: WS_URL,
    appName,
    onLogout: handleLogout,
    onShowSettings: showSettings,
    onShowAgentMonitor: showAgentMonitor,
  });

  chatApp.show();

  // 创建设置面板，并设置快捷键变更回调
  settingsPanel = new SettingsPanel({
    token: state.token,
    apiBaseUrl: API_BASE,
    onSendKeyModeChange: (mode) => {
      // 当用户在设置中更改快捷键模式时，更新 InputBox
      if (chatApp) {
        chatApp.getInputBox().setSendKeyMode(mode);
      }
    },
  });
}

/**
 * 显示设置面板
 */
function showSettings(): void {
  if (!settingsPanel || !chatApp) return;

  // 同步当前快捷键模式到设置面板
  const currentMode = chatApp.getInputBox().getSendKeyMode();
  settingsPanel.setSendKeyMode(currentMode);

  settingsPanel.showWithOutsideClick();
}

/**
 * 显示 Agent 监控界面
 */
function showAgentMonitor(): void {
  if (!state.token) {
    showLoginScreen();
    return;
  }

  agentMonitor = new AgentMonitor({
    token: state.token,
    apiBaseUrl: API_BASE,
    onBack: () => {
      agentMonitor?.destroy();
      agentMonitor = null;
      showChatApp();
    },
  });

  agentMonitor.show();

  // 设置刷新按钮事件
  const refreshBtn = document.getElementById('refresh-agents-btn');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => {
      agentMonitor?.loadStatus();
    });
  }
}

/**
 * 处理登出
 */
function handleLogout(): void {
  state.token = null;
  state.chatJid = null;
  state.isAuthenticated = false;

  storage.clearToken();
  storage.clearChatJid();

  chatApp?.destroy();
  chatApp = null;
  settingsPanel = null;

  showLoginScreen();
}

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
